import React from "react";
import { Box, FormHelperText, Modal, Paper, Stack } from "@mui/material";
import styled from "styled-components";
import request from "src/utils/request";
import { ToastContext } from "src/components/providers/toast-provider";
import AddInviteStepper from "src/scenes/teams/team/add-invite-stepper";
import { useTeam } from "./team-provider";
import useTeamAction from "../hooks/use-team-action";

const ModalContainer = styled(Paper)`
    position: absolute;
    top: 50%;
    left: 50%;
    transform: translate(-50%, -50%);
`;

const Title = styled.h1`
    font-size: 18px;
    font-weight: 500;
    padding: 0;
    margin: 0;
`;

const SubTitle = styled.h2`
    font-size: 12px;
    font-weight: 300;
    padding: 0;
    margin: 0;
    color: ${({ theme }) => theme.palette.secondary.main};
    max-width: 28rem;
`;

const ResponseText = styled(FormHelperText)`
    color: ${({ theme }) => theme.palette.warning.main};
`;

const TeamMemberAddModal = ({ team, open, handleClose }) => {
    const [response, setResponse] = React.useState({ error: undefined, message: undefined });
    const { setMessage, setSeverity, setIsToastTriggered } = React.useContext(ToastContext);
    const [usersToAdd, setUsersToAdd] = React.useState([]);
    const [validationBlocker, setValidationBlocker] = React.useState(false);
    const [addToPlanNotification, setAddToPlanNotification] = React.useState(false);

    const emailChecker = (input) => {
        const regexEmail =
            /^[a-zA-Z0-9.!#$%&'*+/=?^_`{|}~-]+@[a-zA-Z0-9](?:[a-zA-Z0-9-]{0,61}[a-zA-Z0-9])?(?:\.[a-zA-Z0-9](?:[a-zA-Z0-9-]{0,61}[a-zA-Z0-9])?)*$/gi;

        return regexEmail.test(input);
    };

    React.useEffect(() => {
        // eslint-disable-next-line array-callback-return
        usersToAdd.map((item) => {
            if (typeof item === "string") {
                const isEmail = emailChecker([item]);
                if (!isEmail) {
                    setValidationBlocker(true);
                } else {
                    setValidationBlocker(false);
                    setAddToPlanNotification(true);
                }
            } else {
                setValidationBlocker(false);
                setAddToPlanNotification(false);
            }
        });
    }, [usersToAdd]);

    const teamApi = useTeam();

    if (!team) return null;

    const addMembersAction = useTeamAction(
        async () => {
            const teamId = teamApi.data.id;
            const addUsers = usersToAdd.reduce((acc, user) => {
                if (typeof user === "object" && user.email && user.isRegistered === true) {
                    acc.push(user.email);
                }
                return acc;
            }, []);
            const addUnregisteredUsers = usersToAdd.reduce((acc, user) => {
                if (typeof user === "string") {
                    acc.push({ email: user, invite: !!user.invite });
                } else if (typeof user === "object" && user.email && user.isRegistered === false) {
                    acc.push({
                        email: user.email,
                        invite: !!user.invite,
                    });
                }
                return acc;
            }, []);
            if (addUsers.length > 0) {
                await request(`/api/invites/invite-team`, {
                    method: "POST",
                    body: JSON.stringify({ targetUnit: teamId, emails: addUsers }),
                });
            }
            if (addUnregisteredUsers.length > 0) {
                await request(`/api/teams/${teamId}/temp-members`, {
                    method: "PUT",
                    body: JSON.stringify({ members: addUnregisteredUsers }),
                });
            }
            teamApi.refetch(true);
            setMessage(`Users successfully added.`);
            setSeverity("success");
            setIsToastTriggered(true);
        },
        () => {
            setMessage(`You can not perform this action on demo mode`);
            setSeverity("warning");
            setIsToastTriggered(true);
        }
    );

    const clearError = () => {
        setResponse({ error: undefined, message: undefined });
    };

    const closeModal = () => {
        setUsersToAdd([]);
        setResponse({ error: undefined, message: undefined });
        handleClose();
    };

    const handleSubmit = async (e) => {
        e.stopPropagation();
        e.preventDefault();
        setIsToastTriggered(false);
        try {
            addMembersAction();
            setTimeout(closeModal, 1000);
        } catch (error) {
            setMessage(`There was an error adding the users to the team`);
            setSeverity("error");
            setIsToastTriggered(true);
        }
    };
    return (
        <Modal
            open={open}
            onClose={closeModal}
            aria-labelledby="team-member-add-modal-title"
            aria-describedby="team-member-add-modal-description"
        >
            <ModalContainer>
                <Box p={3}>
                    <Stack spacing={2} alignItems="flex-start">
                        <Title>Add Members to {team.name}</Title>
                        <SubTitle>
                            Add users within your organisation automatically to your team or invite
                            and add new users.
                        </SubTitle>
                        <AddInviteStepper
                            usersToAdd={usersToAdd}
                            setUsersToAdd={setUsersToAdd}
                            validationBlocker={validationBlocker}
                            clearError={clearError}
                            handleSubmit={handleSubmit}
                            teamApi={teamApi}
                        />
                        {addToPlanNotification && (
                            <ResponseText color="success">
                                Users will be added to team plan upon joining the platform
                            </ResponseText>
                        )}
                        {validationBlocker && (
                            <ResponseText color="warning">Please add valid emails</ResponseText>
                        )}
                        {response && (
                            <ResponseText error={response.error} color="primary">
                                {response.message}
                            </ResponseText>
                        )}
                    </Stack>
                </Box>
            </ModalContainer>
        </Modal>
    );
};

export default TeamMemberAddModal;
